import React from 'react';

import { serviceCodeDropdownOptions } from '@/components/PersonInsurancePolicy/NewVOB/serviceCodeOptions';
import { ExternalVerificationSource } from '@/types/externalVerification';
import { PillSwitch, PillSwitchOption, Select, Toggle, VendorLogo } from '@/ui';

import './styles.css';
import type { Props } from './types';

export const QUICK_SERVICE_CODES = ['30', 'AI', 'MH'];

const quickCodeLabel = (code: string) =>
  serviceCodeDropdownOptions.find(({ value }) => value === code)?.label ?? code;

const AutomatedInsuranceOptionsForm = ({ value, onChange, serviceCodeHint, className }: Props) => (
  <div className={`flex flex-col gap-4 ${className ?? ''}`}>
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-uniform-gray-700">Vendor</span>
      <PillSwitch
        value={value.vendor}
        onChange={(vendor) => onChange({ ...value, vendor: vendor as ExternalVerificationSource })}
        className="self-start">
        <PillSwitchOption value={ExternalVerificationSource.STEDI}>
          <VendorLogo source="STEDI" size="sm" showName />
        </PillSwitchOption>
        <PillSwitchOption value={ExternalVerificationSource.FRONTRUNNER}>
          <VendorLogo source="FRONTRUNNER" size="sm" showName />
        </PillSwitchOption>
      </PillSwitch>
    </div>

    <div className="flex flex-col gap-1.5">
      <Select
        label="Service code"
        options={serviceCodeDropdownOptions}
        selectedOption={
          serviceCodeDropdownOptions.find(({ value: code }) => code === value.serviceCode) ?? null
        }
        onChange={(option) => option && onChange({ ...value, serviceCode: option.value })}
        placeholder="Select service code"
      />
      {serviceCodeHint && <span className="text-xs text-uniform-gray-500">{serviceCodeHint}</span>}
      <div className="flex flex-wrap gap-1.5">
        {QUICK_SERVICE_CODES.map((code) => {
          const isSelected = value.serviceCode === code;
          return (
            <button
              key={code}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onChange({ ...value, serviceCode: code })}
              className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                isSelected
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-uniform-gray-300 bg-white text-uniform-gray-600'
              }`}>
              {quickCodeLabel(code)}
            </button>
          );
        })}
      </div>
    </div>

    <div className="flex items-center justify-between gap-3">
      <span className="text-sm font-medium text-uniform-gray-700">Use SSN for verification</span>
      <Toggle
        size="sm"
        checked={value.includeSsn}
        onChange={(includeSsn) => onChange({ ...value, includeSsn })}
        aria-label="Use SSN for verification"
      />
    </div>
  </div>
);

export default React.memo(AutomatedInsuranceOptionsForm);
